import { createFileRoute } from "@tanstack/react-router";
import BlogPost from "@/pages/BlogPost";
import { blogPostMeta } from "@/lib/blog-meta";
import { articleBySlug, areaName } from "@/data/knowledge";
import { notFound } from "@tanstack/react-router";
import { buildBreadcrumb, ldJson } from "@/lib/site-schema";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => { if (!blogPostMeta[params.slug]) throw notFound(); },
  head: ({ params }) => {
    const meta = blogPostMeta[params.slug];

    if (!meta) {
      return {
        meta: [
          { name: "description", content: "Este artículo no está disponible en Conocimiento de SEOEVOLUTION." },
          { property: "og:title", content: "Artículo no encontrado | SEOEVOLUTION" },
          { property: "og:description", content: "Consulta los recursos de Conocimiento de SEOEVOLUTION." },
          { property: "og:type", content: "website" },
          { name: "twitter:card", content: "summary" },
          { title: "Artículo no encontrado | SEOEVOLUTION" },
          { name: "robots", content: "noindex" },
        ],
      };
    }

    const article = articleBySlug(params.slug);
    const image = article?.image;
    const hasAbsoluteHttpsImage = typeof image === "string" && image.startsWith("https://");

    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: meta.title,
      description: meta.description,
      ...(hasAbsoluteHttpsImage ? { image } : {}),
      articleSection: article ? areaName(article.area) : undefined,
      author: {
        "@type": "Person",
        name: meta.author,
        url: "https://agenciaseoevolution.com/angel-ruz",
        "@id": "https://agenciaseoevolution.com/angel-ruz#person",
      },
      publisher: {
        "@type": "Organization",
        name: "SEOEVOLUTION",
        url: "https://agenciaseoevolution.com",
        logo: {
          "@type": "ImageObject",
          url: "https://agenciaseoevolution.com/logo.png",
        },
      },
      datePublished: meta.datePublished,
      dateModified: meta.dateModified || meta.datePublished,
      mainEntityOfPage: meta.canonicalUrl,
      inLanguage: "es",
      keywords: meta.keywords,
    };

    const breadcrumbSchema = buildBreadcrumb([
      { name: "Conocimiento", path: "/blog" },
      { name: meta.title, path: `/blog/${params.slug}` },
    ]);

    return {
      meta: [
        { title: meta.title },
        { name: "description", content: meta.description },
        { name: "keywords", content: meta.keywords },
        { name: "author", content: meta.author },
        { property: "og:type", content: "article" },
        { property: "og:title", content: meta.title },
        { property: "og:description", content: meta.description },
        { property: "og:url", content: meta.canonicalUrl },
        ...(hasAbsoluteHttpsImage
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: meta.title },
        { name: "twitter:description", content: meta.description },
      ],
      links: [{ rel: "canonical", href: meta.canonicalUrl }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(articleSchema),
        },
        ldJson(breadcrumbSchema),
        // Evita duplicar Article/BreadcrumbList ya emitidos arriba.
        ...(meta.extraSchemas || [])
          .filter((schema) => {
            const type = (schema as { "@type"?: string })["@type"];
            return type !== "Article" && type !== "BreadcrumbList";
          })
          .map((schema) => ({
            type: "application/ld+json",
            children: JSON.stringify(schema),
          })),
      ],
    };
  },
  component: BlogPost,
});
